import { Pagination } from '@mui/material';
import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { CiEdit } from "react-icons/ci";
import { MdDeleteOutline } from 'react-icons/md';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { BACKEND_SERVER_URL } from '../../../http/http-config';
import '../common-css/Table.css';
import { deleteItem, showTableResponse, sortInAlphabeticalOrder } from '../common-helpers/commonServices';
import CUETSubjectModal from './CUETSubjectModal';
import CUET_SectionModal from './CUET_SectionModal';
import CUET_TopicModal from './CUET_TopicModal';
import CUET_QuestionBankModal from './CUET_QuestionModalFromAllSubjects';

const CUET_AllSubjects = () => {
    const subjectRef = useRef();
    const sectionRef = useRef();
    const topicRef = useRef();
    const questionModalRef = useRef();

    const [subjects, setSubjects] = useState([]);
    const [sections, setSections] = useState([]);
    const [topics, setTopics] = useState([]);

    const [selectedSubjects, setSelectedSubjects] = useState([]);
    const [selectedSections, setSelectedSections] = useState([]);
    const [selectedTopicsDropdown, setSelectedTopicsDropdown] = useState([]);

    const [selectedExamType, setSectedExamType] = useState(() => sessionStorage.getItem('cuet_selectedExamType') || "All SUBJECT");
    const [selectedSubjectType, setSelectedSubjectType] = useState(() => {
        const saved = sessionStorage.getItem('cuet_selectedSubjectType');
        return saved ? JSON.parse(saved) : { SubjectType: "All SUBJECT NAME" };
    });
    const [selectedSection, setSelectedSection] = useState(() => {
        const saved = sessionStorage.getItem('cuet_selectedSection');
        return saved ? JSON.parse(saved) : { Section: "All TOPICS" };
    });
    const [selectedTopic, setSelectedTopic] = useState(() => {
        const saved = sessionStorage.getItem('cuet_selectedTopic');
        return saved ? JSON.parse(saved) : { Topic: "All SUB TOPIC" };
    });

    const [currentPage, setCurrentPage] = useState(1);
    const [checked, setChecked] = useState([]);
    const [loadData, setLoadData] = useState(false);

    const getExamTypeLabel = (id) => {
        if (id === "Section-IA") return "Language Subjects";
        if (id === "Section-II") return "Domain-Specific Subjects";
        if (id === "Section-III") return "General Aptitude Test";
        return id || '';
    };

    const multiSortTopics = (array) => {
        return array.sort((a, b) => {
            const examTypeA = getExamTypeLabel(a.ExamType).toLowerCase();
            const examTypeB = getExamTypeLabel(b.ExamType).toLowerCase();
            if (examTypeA < examTypeB) return -1;
            if (examTypeA > examTypeB) return 1;

            const subjectA = a.SubjectType?.toLowerCase() || '';
            const subjectB = b.SubjectType?.toLowerCase() || '';
            if (subjectA < subjectB) return -1;
            if (subjectA > subjectB) return 1;

            const sectionA = a.Section?.toLowerCase() || '';
            const sectionB = b.Section?.toLowerCase() || '';
            if (sectionA < sectionB) return -1;
            if (sectionA > sectionB) return 1;

            const topicA = a.Topic?.toLowerCase() || '';
            const topicB = b.Topic?.toLowerCase() || '';
            if (topicA < topicB) return -1;
            if (topicA > topicB) return 1;

            return 0;
        });
    };

    // Fetch all data
    useEffect(() => {
        const response = axios.all([
            axios.get(BACKEND_SERVER_URL + '/cuet/subjects'),
            axios.get(BACKEND_SERVER_URL + '/cuet/sections'),
            axios.get(BACKEND_SERVER_URL + '/cuet/topics')
        ]);

        response.then(axios.spread((subjectsResponse, sectionsResponse, topicsResponse) => {
            let fetchedSubjects = subjectsResponse.data.subjects;
            let fetchedSections = sectionsResponse.data.sections;
            let fetchedTopics = topicsResponse.data.topics;

            setSubjects(fetchedSubjects);
            setSections(sortInAlphabeticalOrder(fetchedSections, 'Section'));
            setTopics(multiSortTopics(fetchedTopics));

            // Re-apply filters from storage
            const savedExam = sessionStorage.getItem('cuet_selectedExamType') || "All SUBJECT";
            const savedSubject = sessionStorage.getItem('cuet_selectedSubjectType') ? JSON.parse(sessionStorage.getItem('cuet_selectedSubjectType')) : { SubjectType: "All SUBJECT NAME" };
            const savedSection = sessionStorage.getItem('cuet_selectedSection') ? JSON.parse(sessionStorage.getItem('cuet_selectedSection')) : { Section: "All TOPICS" };
            const savedTopic = sessionStorage.getItem('cuet_selectedTopic') ? JSON.parse(sessionStorage.getItem('cuet_selectedTopic')) : { Topic: "All SUB TOPIC" };

            let finalChecked = [...multiSortTopics(fetchedTopics)];

            if (savedExam !== 'All Exam Types' && savedExam !== "All SUBJECT") {
                let filteredSubjects = fetchedSubjects.filter(subject => subject.ExamType === savedExam);
                setSelectedSubjects(sortInAlphabeticalOrder(filteredSubjects, 'SubjectType'));
                finalChecked = finalChecked.filter(topic => topic.ExamType === savedExam);
            } else {
                setSelectedSubjects([]);
            }

            if (savedSubject && savedSubject.SubjectType !== 'All SUBJECT NAME') {
                let filteredSections = fetchedSections.filter(sec => sec.SubjectTypeID === savedSubject.Sn);
                setSelectedSections(sortInAlphabeticalOrder(filteredSections, 'Section'));
                finalChecked = finalChecked.filter(topic => topic.SubjectTypeID === savedSubject.Sn);
            } else {
                setSelectedSections([]);
            }

            if (savedSection && savedSection.Section !== 'All TOPICS') {
                let filteredTopicsList = fetchedTopics.filter(topic => topic.SectionID === savedSection.Sn);
                setSelectedTopicsDropdown(multiSortTopics(filteredTopicsList));
                finalChecked = finalChecked.filter(topic => topic.SectionID === savedSection.Sn);
            } else {
                setSelectedTopicsDropdown([]);
            }

            if (savedTopic && savedTopic.Topic !== 'All SUB TOPIC') {
                finalChecked = finalChecked.filter(topic => topic.Sn === savedTopic.Sn);
            }

            setChecked(finalChecked);
        })).catch((error) => {
            console.log(error);
            toast.error(error.response?.data?.message || "An error occurred while fetching data.");
        });

    }, [loadData])

    const pageSize = 100;
    const lastIndex = currentPage * pageSize;
    const firstIndex = lastIndex - pageSize;
    const records = checked?.slice(firstIndex, lastIndex);
    const npage = Math.ceil(checked?.length / pageSize)
    const number = [...Array(npage + 1).keys()].slice(1)

    const selectExamTypeHandler = (examType) => {
        setSelectedSubjectType({ SubjectType: "All SUBJECT NAME" });
        sessionStorage.setItem('cuet_selectedSubjectType', JSON.stringify({ SubjectType: "All SUBJECT NAME" }));
        setSelectedSection({ Section: "All TOPICS" });
        sessionStorage.setItem('cuet_selectedSection', JSON.stringify({ Section: "All TOPICS" }));
        setSectedExamType(examType);
        sessionStorage.setItem('cuet_selectedExamType', examType);

        if (examType === 'All Exam Types') {
            setChecked([...multiSortTopics(topics)]);
            setSelectedSubjects([]);
            setSelectedSections([]);
            setSelectedTopicsDropdown([]);
            setSelectedTopic({ Topic: "All SUB TOPIC" });
            sessionStorage.setItem('cuet_selectedTopic', JSON.stringify({ Topic: "All SUB TOPIC" }));
            return;
        }

        let filteredSubjects = subjects.filter(subject => subject.ExamType === examType);
        setSelectedSubjects(sortInAlphabeticalOrder(filteredSubjects, 'SubjectType'));

        let filteredTopics = topics.filter(topic => topic.ExamType === examType);
        setChecked([...multiSortTopics(filteredTopics)]);
        setSelectedTopicsDropdown([]);
        setSelectedTopic({ Topic: "All SUB TOPIC" });
        sessionStorage.setItem('cuet_selectedTopic', JSON.stringify({ Topic: "All SUB TOPIC" }));
    }

    const selectSubjectTypeHandler = (subjectType) => {
        if (!subjectType || subjectType.SubjectType === 'All SUBJECT NAME') {
            let filteredTopics = topics.filter(topic => topic.ExamType === selectedExamType);
            setChecked(filteredTopics.length > 0 ? [...multiSortTopics(filteredTopics)] : [...multiSortTopics(topics)]);
            setSelectedSubjectType({ SubjectType: "All SUBJECT NAME", Sn: 0 });
            sessionStorage.setItem('cuet_selectedSubjectType', JSON.stringify({ SubjectType: "All SUBJECT NAME", Sn: 0 }));
            setSelectedSections([]);
            setSelectedSection({ Section: "All TOPICS", Sn: 0 });
            sessionStorage.setItem('cuet_selectedSection', JSON.stringify({ Section: "All TOPICS", Sn: 0 }));
            setSelectedTopicsDropdown([]);
            setSelectedTopic({ Topic: "All SUB TOPIC", Sn: 0 });
            sessionStorage.setItem('cuet_selectedTopic', JSON.stringify({ Topic: "All SUB TOPIC", Sn: 0 }));
            return;
        }

        setSelectedSubjectType(subjectType);
        sessionStorage.setItem('cuet_selectedSubjectType', JSON.stringify(subjectType));
        setSelectedSection({ Section: "All TOPICS", Sn: 0 });
        sessionStorage.setItem('cuet_selectedSection', JSON.stringify({ Section: "All TOPICS", Sn: 0 }));
        let filteredSections = sections.filter(sec => sec.SubjectTypeID === subjectType.Sn);
        setSelectedSections(sortInAlphabeticalOrder(filteredSections, 'Section'));

        let filteredTopics = topics.filter(topic => topic.SubjectTypeID === subjectType.Sn);
        setChecked([...multiSortTopics(filteredTopics)]);
        setSelectedTopicsDropdown([]);
        setSelectedTopic({ Topic: "All SUB TOPIC", Sn: 0 });
        sessionStorage.setItem('cuet_selectedTopic', JSON.stringify({ Topic: "All SUB TOPIC", Sn: 0 }));
    }

    const selectSectionHandler = (section) => {
        if (!section || section.Section === 'All TOPICS') {
            let filteredTopics = topics.filter(topic => topic.SubjectTypeID === selectedSubjectType.Sn);
            setChecked([...multiSortTopics(filteredTopics)]);
            setSelectedSection({ Section: "All TOPICS", Sn: 0 });
            sessionStorage.setItem('cuet_selectedSection', JSON.stringify({ Section: "All TOPICS", Sn: 0 }));
            setSelectedTopicsDropdown([]);
            setSelectedTopic({ Topic: "All SUB TOPIC", Sn: 0 });
            sessionStorage.setItem('cuet_selectedTopic', JSON.stringify({ Topic: "All SUB TOPIC", Sn: 0 }));
            return;
        }

        setSelectedSection(section);
        sessionStorage.setItem('cuet_selectedSection', JSON.stringify(section));
        let filteredTopics = topics.filter(topic => topic.SectionID === section.Sn);
        setSelectedTopicsDropdown(multiSortTopics(filteredTopics));
        setChecked([...multiSortTopics(filteredTopics)]);
        setSelectedTopic({ Topic: "All SUB TOPIC", Sn: 0 });
        sessionStorage.setItem('cuet_selectedTopic', JSON.stringify({ Topic: "All SUB TOPIC", Sn: 0 }));
    }

    const selectTopicHandler = (topic) => {
        if (topic.Topic === 'All SUB TOPIC') {
            let filteredTopics = topics.filter(t => t.SectionID === selectedSection.Sn);
            setChecked([...multiSortTopics(filteredTopics)]);
            setSelectedTopic(topic);
            sessionStorage.setItem('cuet_selectedTopic', JSON.stringify(topic));
            return;
        }

        setSelectedTopic(topic);
        sessionStorage.setItem('cuet_selectedTopic', JSON.stringify(topic));
        setChecked([topic]);
    }

    // Helper functions to find entities for editing
    const findSubject = (subjectID) => subjects.find(s => s.Sn === subjectID);
    const findSection = (sectionID) => sections.find(s => s.Sn === sectionID);
    const findTopic = (topicID) => topics.find(t => t.Sn === topicID);

    return (
        <>
            <div className="row">
                <div className="col-xl-12">
                    <div className="row">
                        <div className="col-xl-12">
                            <div className=" page-title p-2 mb-2">
                                <div className="row w-100 d-flex justify-content-between">
                                    <div className='col-12 d-flex flex-wrap justify-content-start align-items-center gap-2'>
                                        <div className="col-12 col-md-auto d-flex flex-wrap gap-2 justify-content-start">
                                            <button type="button" className="btn btn-dark btn-xs" style={{ whiteSpace: 'nowrap', backgroundColor: '#274065', borderColor: '#274065' }}
                                                onClick={() => subjectRef.current.openModal({ ExamType: selectedExamType !== 'All Exam Types' ? selectedExamType : 'Section-IA' })}>+ SUBJECT NAME</button>
                                            <button type="button" className="btn btn-info btn-xs text-white" style={{ whiteSpace: 'nowrap', backgroundColor: '#00a99d', borderColor: '#00a99d' }}
                                                onClick={() => sectionRef.current.openModal({
                                                    ExamType: selectedExamType !== 'All Exam Types' ? selectedExamType : 'Section-IA',
                                                    SubjectType: selectedSubjectType.SubjectType !== 'All SUBJECT NAME' ? selectedSubjectType.SubjectType : '',
                                                    SubjectTypeID: selectedSubjectType.Sn > 0 ? selectedSubjectType.Sn : 0
                                                })}>+ TOPICS</button>
                                            <button type="button" className="btn btn-primary btn-xs" style={{ whiteSpace: 'nowrap', backgroundColor: '#4d44b5', borderColor: '#4d44b5' }}
                                                onClick={() => topicRef.current.openModal({
                                                    ExamType: selectedExamType !== 'All Exam Types' ? selectedExamType : 'Section-IA',
                                                    SubjectType: selectedSubjectType.SubjectType !== 'All SUBJECT NAME' ? selectedSubjectType.SubjectType : '',
                                                    SubjectTypeID: selectedSubjectType.Sn > 0 ? selectedSubjectType.Sn : 0,
                                                    Section: selectedSection.Section !== 'All TOPICS' ? selectedSection.Section : '',
                                                    SectionID: selectedSection.Sn > 0 ? selectedSection.Sn : 0
                                                })}>+ SUB TOPIC</button>
                                        </div>

                                        <div className="col-12 col-md-auto d-flex flex-wrap justify-content-start align-items-center gap-2">
                                            <div style={{ minWidth: '170px' }}>
                                                <Select
                                                    value={[
                                                        { value: 'All Exam Types', label: 'All SUBJECT' },
                                                        { value: 'Section-IA', label: 'Language Subjects' },
                                                        { value: 'Section-II', label: 'Domain-Specific Subjects' },
                                                        { value: 'Section-III', label: 'General Aptitude Test' }
                                                    ].find(opt => opt.value === selectedExamType)}
                                                    onChange={(selectedOption) => selectExamTypeHandler(selectedOption.value)}
                                                    options={[
                                                        { value: 'All Exam Types', label: 'All SUBJECT' },
                                                        { value: 'Section-IA', label: 'Language Subjects' },
                                                        { value: 'Section-II', label: 'Domain-Specific Subjects' },
                                                        { value: 'Section-III', label: 'General Aptitude Test' }
                                                    ]}
                                                    styles={{ control: (provided) => ({ ...provided, borderColor: '#17a2b8', minHeight: '35px', fontSize: '13px' }) }}
                                                />
                                            </div>
                                            <div className="col-12 col-sm-5 col-md-2" style={{ minWidth: '160px' }}>
                                                <Select
                                                    isDisabled={selectedExamType === 'All Exam Types'}
                                                    value={{ value: selectedSubjectType, label: selectedSubjectType.SubjectType }}
                                                    onChange={(selectedOption) => selectSubjectTypeHandler(selectedOption.value)}
                                                    options={[{ Sn: 0, SubjectType: 'All SUBJECT NAME' }, ...selectedSubjects]?.map((subject) => ({ value: subject, label: subject.SubjectType }))}
                                                    styles={{ control: (provided) => ({ ...provided, borderColor: '#17a2b8', minHeight: '35px', fontSize: '13px' }) }}
                                                />
                                            </div>
                                            <div className="col-12 col-sm-5 col-md-2" style={{ minWidth: '160px' }}>
                                                <Select
                                                    isDisabled={selectedSubjectType.SubjectType === 'All SUBJECT NAME'}
                                                    value={{ value: selectedSection, label: selectedSection.Section }}
                                                    onChange={(selectedOption) => selectSectionHandler(selectedOption.value)}
                                                    options={[{ Sn: 0, Section: 'All TOPICS' }, ...selectedSections]?.map((section) => ({ value: section, label: section.Section }))}
                                                    styles={{ control: (provided) => ({ ...provided, borderColor: '#17a2b8', minHeight: '35px', fontSize: '13px' }) }}
                                                />
                                            </div>
                                            <div className="col-12 col-sm-5 col-md-2" style={{ minWidth: '160px' }}>
                                                <Select
                                                    isDisabled={selectedSection.Section === 'All TOPICS'}
                                                    value={{ value: selectedTopic, label: selectedTopic.Topic }}
                                                    onChange={(selectedOption) => selectTopicHandler(selectedOption.value)}
                                                    options={[{ Sn: 0, Topic: 'All SUB TOPIC' }, ...selectedTopicsDropdown]?.map((topic) => ({ value: topic, label: topic.Topic }))}
                                                    styles={{ control: (provided) => ({ ...provided, borderColor: '#17a2b8', minHeight: '35px', fontSize: '13px' }) }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-xl-12 wow fadeInUp" data-wow-delay="1.5s">
                            <div className="d-sm-flex text-center justify-content-between align-items-center">
                                <div className='dataTables_info text-dark'>
                                    Records: {checked.length}
                                </div>
                                {checked.length > pageSize && (
                                    <div className="dataTables_paginate paging_simple_numbers justify-content-center mb-0 pt-2">
                                        <Pagination count={number.length} variant="outlined" color='primary' size="small" page={currentPage} onChange={(e, p) => setCurrentPage(parseInt(p))} />
                                    </div>
                                )}
                            </div>

                            <div className="table-responsive full-data" style={{ paddingBottom: "1px", transform: 'rotateX(180deg)' }}>
                                <div id="example-student_wrapper" className="dataTables_wrapper no-footer">
                                    <table className="table-responsive-lg table display dataTablesCard student-tab dataTable no-footer mt-0 mb-0" style={{ transform: 'rotateX(180deg)' }}>
                                        <thead style={{ backgroundColor: "#cccccc" }} >
                                            <tr className='text-center border border-left'>
                                                <th className='custom-table-header p-2 text-start'>Sl.No.</th>
                                                <th className='custom-table-header p-2 text-start'>All SUBJECT</th>
                                                <th className='custom-table-header p-2 text-start'>SUBJECT NAME</th>
                                                <th className='custom-table-header p-2 text-start'>TOPICS</th>
                                                <th className='custom-table-header p-2 text-start'>SUB TOPIC</th>
                                                <th className='custom-table-header p-2 text-center'>COUNT</th>
                                                <th className='custom-table-header p-2 text-center'>ACTION</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {records.length > 0 ? records.map((item, ind) => (
                                                <tr key={ind} className={`${ind % 2 == 1 ? 'custom row-height' : 'row-height'}`} >
                                                    <td className='p-1 text-center' style={{ border: "1px solid #cccccc", width: "50px" }}>
                                                        <h6 className="mb-0 " style={{ fontSize: '12px' }}>
                                                            {firstIndex + ind + 1}
                                                        </h6>
                                                    </td>
                                                    <td className='p-1 text-center' style={{ border: "1px solid #cccccc", width: "150px" }}>
                                                        <h6 className="mb-0 " style={{ fontSize: '12px' }}>
                                                            {item.ExamType === "Section-IA" ? "Language Subjects" : item.ExamType === "Section-II" ? "Domain-Specific Subjects" : item.ExamType === "Section-III" ? "General Aptitude Test" : item.ExamType}
                                                        </h6>
                                                    </td>
                                                    <td className='p-1' style={{ border: "1px solid #cccccc", width: "180px" }}>
                                                        <div className="d-flex justify-content-between align-items-center px-2">
                                                            <h6 className="mb-0" style={{ fontSize: '13px', fontWeight: 'bold' }}>{item.SubjectType}
                                                                <CiEdit cursor='pointer' title='Edit SUBJECT NAME' size={18} className='text-info ms-3'
                                                                    onClick={() => subjectRef.current.openModal(findSubject(item.SubjectTypeID))} />
                                                                <MdDeleteOutline color="red" title='Delete SUBJECT NAME' cursor='pointer' size={18} className='ms-2'
                                                                    onClick={() => deleteItem(item.SubjectTypeID, 'SUBJECT NAME', '/cuet/deleteSubject/', setLoadData, loadData)} />
                                                            </h6>
                                                        </div>
                                                    </td>
                                                    <td className='p-1' style={{ border: "1px solid #cccccc", width: "180px" }}>
                                                        <div className="d-flex justify-content-between align-items-center px-2">
                                                            <h6 className="mb-0" style={{ fontSize: '13px', fontWeight: 'bold' }}>{item.Section}
                                                                <CiEdit cursor='pointer' title='Edit TOPICS' size={18} className='text-info ms-3'
                                                                    onClick={() => sectionRef.current.openModal(findSection(item.SectionID))} />
                                                                <MdDeleteOutline color="red" title='Delete TOPICS' cursor='pointer' size={18} className='ms-2'
                                                                    onClick={() => deleteItem(item.SectionID, 'TOPICS', '/cuet/deleteSection/', setLoadData, loadData)} />
                                                            </h6>
                                                        </div>
                                                    </td>
                                                    <td className='p-1' style={{ border: "1px solid #cccccc" }}>
                                                        <div className="d-flex justify-content-between align-items-center px-2">
                                                            <h6 className="mb-0" style={{ fontSize: '13px', fontWeight: 'bold' }}>{item.Topic}
                                                                <CiEdit cursor='pointer' title='Edit SUB TOPIC' size={18} className='text-info ms-3'
                                                                    onClick={() => topicRef.current.openModal(item)} />
                                                                <MdDeleteOutline color="red" title='Delete SUB TOPIC' cursor='pointer' size={18} className='ms-2'
                                                                    onClick={() => deleteItem(item.Sn, 'SUB TOPIC', '/cuet/deleteTopic/', setLoadData, loadData)} />
                                                            </h6>
                                                        </div>
                                                    </td>
                                                    <td className='p-1' style={{ border: "1px solid #cccccc", width: "180px" }}>
                                                        <div className="d-flex justify-content-center align-items-center gap-2 p-1">
                                                            <div className="d-flex align-items-center gap-1">
                                                                <span style={{ fontSize: '10px', fontWeight: '600' }}>Asigned:</span>
                                                                <span className="badge badge-success py-1 px-1" style={{ fontSize: '10px', minWidth: '25px' }}>{item.NoOfQuestion ?? 0}</span>
                                                            </div>
                                                            <div className="d-flex align-items-center gap-1">
                                                                <span style={{ fontSize: '10px', fontWeight: '600' }}>Available:</span>
                                                                <span className="badge badge-info py-1 px-1" style={{ fontSize: '10px', minWidth: '25px' }}>{item.AvailableQuestions ?? 0}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className='p-1 text-center' style={{ border: "1px solid #cccccc", width: "100px" }}>
                                                        <button type="button" className="btn btn-primary btn-xxs shadow"
                                                            onClick={() => questionModalRef.current.openModal({
                                                                examType: item.ExamType,
                                                                subjectType: item.SubjectType,
                                                                subjectId: item.SubjectTypeID,
                                                                section: item.Section,
                                                                sectionId: item.SectionID,
                                                                topic: item.Topic,
                                                                topicId: item.Sn
                                                            })}
                                                        >Add Question</button>
                                                    </td>
                                                </tr>
                                            )) :
                                                <tr>
                                                    <td colSpan="7" className="text-center p-5">
                                                        {showTableResponse(topics, checked)}
                                                    </td>
                                                </tr>}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <CUETSubjectModal ref={subjectRef} loadSubjects={loadData} setSubjectLoading={setLoadData} />
            <CUET_SectionModal ref={sectionRef} loadSubjects={loadData} setSubjectLoading={setLoadData} />
            <CUET_TopicModal ref={topicRef} loadSubjects={loadData} setSubjectLoading={setLoadData} />
            <CUET_QuestionBankModal ref={questionModalRef} loadQuestions={loadData} setLoadQuestions={setLoadData} setQuestionOffset={() => { }} />
        </>
    );
};

export default CUET_AllSubjects;
